/**
 * Contexto global de permissões do painel Admin White Label (/wl)
 *
 * Faz UM ÚNICO fetch via RPC `wl_get_my_permissions` no boot e compartilha
 * com toda a árvore — mesmo padrão do PermissionsContext do tenant.
 *
 * Suporta dois modos:
 *  - Operador WL (registro em wl_users ativo)
 *  - Observador (root_admin, somente leitura)
 */
import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('WlPermissionsContext');

// Estrutura de permissões por menu
export interface WlMenuPermission {
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

interface WlPermissionsState {
  loading: boolean;
  error: boolean;             // Falha técnica no RPC (não confundir com "sem acesso")
  isWlUser: boolean;          // Possui registro em wl_users ativo
  isObserver: boolean;        // root_admin sem cadastro em wl_users
  roleId: string | null;
  roleName: string | null;
  roleSlug: string | null;
  displayName: string | null;
  permissions: Record<string, WlMenuPermission>;
}

interface WlPermissionsContextType extends WlPermissionsState {
  canView: (menuKey: string) => boolean;
  canCreate: (menuKey: string) => boolean;
  canEdit: (menuKey: string) => boolean;
  canDelete: (menuKey: string) => boolean;
  hasAccess: boolean;         // Atalho: isWlUser || isObserver
  refetch: () => Promise<void>;
}

const WlPermissionsContext = createContext<WlPermissionsContextType | null>(null);

const INITIAL_STATE: WlPermissionsState = {
  loading: true,
  error: false,
  isWlUser: false,
  isObserver: false,
  roleId: null,
  roleName: null,
  roleSlug: null,
  displayName: null,
  permissions: {},
};


export function WlPermissionsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState<WlPermissionsState>(INITIAL_STATE);

  const fetchPermissions = useCallback(async () => {
    if (!user?.id) {
      setState({ ...INITIAL_STATE, loading: false });
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: false }));

    try {
      // RPC retorna 1 linha com {is_wl_user, is_observer, role_id, role_name, role_slug, display_name, permissions}
      const { data, error } = await supabase.rpc('wl_get_my_permissions');
      if (error) throw error;

      const row = Array.isArray(data) ? data[0] : null;
      if (!row) {
        setState({ ...INITIAL_STATE, loading: false });
        return;
      }

      setState({
        loading: false,
        error: false,
        isWlUser: Boolean(row.is_wl_user),
        isObserver: Boolean(row.is_observer),
        roleId: row.role_id ?? null,
        roleName: row.role_name ?? null,
        roleSlug: row.role_slug ?? null,
        displayName: row.display_name ?? null,
        permissions: (row.permissions as unknown as Record<string, WlMenuPermission>) ?? {},
      });
    } catch (err) {
      logger.error('Erro ao carregar permissões WL', { err });
      // Marca erro técnico — guards devem tratar diferente de "sem acesso"
      setState({ ...INITIAL_STATE, loading: false, error: true });
    }
  }, [user?.id]);


  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Helpers de verificação granular
  const canView = useCallback((key: string) => state.permissions[key]?.can_view ?? false, [state.permissions]);
  const canCreate = useCallback((key: string) => state.permissions[key]?.can_create ?? false, [state.permissions]);
  const canEdit = useCallback((key: string) => state.permissions[key]?.can_edit ?? false, [state.permissions]);
  const canDelete = useCallback((key: string) => state.permissions[key]?.can_delete ?? false, [state.permissions]);

  return (
    <WlPermissionsContext.Provider value={{
      ...state,
      canView,
      canCreate,
      canEdit,
      canDelete,
      hasAccess: state.isWlUser || state.isObserver,
      refetch: fetchPermissions,
    }}>
      {children}
    </WlPermissionsContext.Provider>
  );
}

/** Hook consumidor do contexto de permissões WL */
export function useWlPermissions() {
  const ctx = useContext(WlPermissionsContext);
  if (!ctx) throw new Error('useWlPermissions deve ser usado dentro de WlPermissionsProvider');
  return ctx;
}
